<template>
  <div class="field-container" v-if="isVisible">
    <label :for="field.title" class="field-label">
      {{ $t(`${translationPrefix}.generator_review.${field.title}.title`) }}
    </label>

    <div class="field-description" v-if="hasDescription">
      {{
        $t(`${translationPrefix}.generator_review.${field.title}.description`)
      }}
    </div>

    <!-- String/Number Input -->
    <input
      v-if="['string', 'number'].includes(field.type)"
      :type="field.type === 'number' ? 'number' : 'text'"
      :id="field.title"
      v-model="localValue"
      class="form-control"
      :class="{ 'is-invalid': v$.localValue.$error }"
      @input="handleInput($event)"
      @blur="v$.localValue.$touch"
    />

    <!-- Date Input -->
    <input
      v-else-if="field.type === 'date'"
      type="date"
      :id="field.title"
      v-model="localValue"
      class="form-control"
      :class="{ 'is-invalid': v$.localValue.$error }"
      @input="handleInput($event)"
      @blur="v$.localValue.$touch"
    />

    <!-- Dropdown -->
    <select
      v-else-if="field.type === 'dropdown'"
      :id="field.title"
      v-model="localValue"
      class="form-select"
      :class="{ 'is-invalid': v$.localValue.$error }"
      @change="handleInput($event)"
      @blur="v$.localValue.$touch"
    >
      <option
        v-for="option in field.options"
        :key="option.label"
        :value="option.value"
      >
        {{
          $t(
            `${translationPrefix}.generator_review.${field.title}.options.${option.label}`
          )
        }}
      </option>
    </select>

    <!-- Boolean Select -->
    <select
      v-else-if="field.type === 'boolean'"
      :id="field.title"
      v-model="localValue"
      class="form-select"
      :class="{ 'is-invalid': v$.localValue.$error }"
      @change="handleInput($event)"
      @blur="v$.localValue.$touch"
    >
      <option :value="true">{{ $t("common.yes") }}</option>
      <option :value="false">{{ $t("common.no") }}</option>
    </select>

    <!-- Checkbox -->
    <div v-else-if="field.type === 'checkbox'" class="form-check">
      <input
        type="checkbox"
        :id="field.title"
        v-model="localValue"
        class="form-check-input"
        :class="{ 'is-invalid': v$.localValue.$error }"
        @change="handleCheckboxChange"
        @blur="v$.localValue.$touch"
      />
    </div>

    <!-- Validation Error -->

    <div v-if="hasError" class="invalid-feedback">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, ref, Ref, unref } from "vue";
import useVuelidate from "@vuelidate/core";
import { useI18n } from "vue-i18n";

export interface GeneratorFieldConfig {
  title: string;
  model: string;
  type:
    | "string"
    | "number"
    | "boolean"
    | "checkbox"
    | "date"
    | "dropdown"
    | "radio"
    | "none";
  options?: { value: string | number | boolean; label: string }[];
  validation?: any[] | ComputedRef<any[]>;
  editable?: boolean;
  visible?: Ref<boolean>;
  condition?: (data: any) => boolean;
}

const props = withDefaults(
  defineProps<{
    field: GeneratorFieldConfig;
    modelValue: string | number | boolean;
    translationPrefix?: string;
  }>(),
  { translationPrefix: "signup.lv" }
);

const emit = defineEmits<{
  "update:modelValue": [value: string | number | boolean];
}>();

const { te } = useI18n();
const hasDescription = computed(() => {
  return te(
    `${props.translationPrefix}.generator_review.${props.field.title}.description`
  );
});
const isVisible =
  props.field.visible === undefined ? true : props.field.visible;

const localValue = ref(props.modelValue);

const rules = computed(() => {
  if (props.field.validation) {
    return {
      localValue: unref(props.field.validation),
    };
  }
  return { localValue: [] };
});

const v$ = useVuelidate(rules, { localValue });

const hasError = computed(() => v$.value.localValue.$error);

const errorMessage = computed(() => {
  return v$.value.localValue.$errors
    ? v$.value.localValue.$errors[0].$message
    : "";
});

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit("update:modelValue", target.value);
};

const handleCheckboxChange = () => {
  emit("update:modelValue", localValue.value);
};
</script>
<style scoped lang="scss">
.field-container {
  margin-bottom: 1rem;
}

.field-label {
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.field-description {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
}
</style>
